import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Componente de Carga Temático de Fibra Óptica
 * Reemplaza los spinners circulares genéricos por un diseño de fibra de alta velocidad.
 */
const FiberLoader = ({
  text = 'Conectando por Fibra Óptica...',
  subtext = 'Sincronizando enlace de alta velocidad...',
  size = 'medium',
  fullPage = false
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: fullPage ? '65vh' : '260px',
        py: 4,
        px: 2,
        width: '100%',
        position: 'relative'
      }}
    >
      <style>{`
        @keyframes fiberPulse {
          0% {
            stroke-dashoffset: 240;
            opacity: 0.3;
          }
          50% {
            opacity: 1;
          }
          100% {
            stroke-dashoffset: 0;
            opacity: 0.3;
          }
        }
        @keyframes glowRing {
          0% {
            transform: scale(0.88);
            box-shadow: 0 0 15px rgba(0, 198, 255, 0.35), inset 0 0 15px rgba(0, 198, 255, 0.35);
          }
          50% {
            transform: scale(1.08);
            box-shadow: 0 0 35px rgba(0, 198, 255, 0.75), inset 0 0 25px rgba(0, 198, 255, 0.75);
          }
          100% {
            transform: scale(0.88);
            box-shadow: 0 0 15px rgba(0, 198, 255, 0.35), inset 0 0 15px rgba(0, 198, 255, 0.35);
          }
        }
        @keyframes laserStream {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        @keyframes signalWave {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.9; }
          50% { transform: translateY(-3px) scale(1.04); opacity: 1; }
        }
      `}</style>

      {/* Contenedor del núcleo de fibra óptica */}
      <Box
        sx={{
          position: 'relative',
          width: size === 'small' ? 90 : 130,
          height: size === 'small' ? 90 : 130,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5
        }}
      >
        {/* Anillo exterior de fotones */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '2px solid rgba(0, 198, 255, 0.4)',
            animation: 'glowRing 2.4s infinite ease-in-out',
            bgcolor: 'rgba(0, 198, 255, 0.04)'
          }}
        />

        {/* SVG de haces de luz de fibra óptica en curva */}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 120 120"
          style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
        >
          <defs>
            <linearGradient id="fiberGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00c6ff" stopOpacity="0.1" />
              <stop offset="50%" stopColor="#0072ff" stopOpacity="1" />
              <stop offset="100%" stopColor="#00c6ff" stopOpacity="0.1" />
            </linearGradient>
            <filter id="glowFiber" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          <path
            d="M 20 60 A 40 40 0 1 1 100 60"
            fill="none"
            stroke="url(#fiberGrad1)"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray="70 170"
            style={{ animation: 'fiberPulse 1.6s infinite linear', filter: 'url(#glowFiber)' }}
          />
          <path
            d="M 100 60 A 40 40 0 1 1 20 60"
            fill="none"
            stroke="url(#fiberGrad1)"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray="70 170"
            style={{ animation: 'fiberPulse 1.6s infinite linear 0.8s', filter: 'url(#glowFiber)' }}
          />
        </svg>

        {/* Núcleo central gow con icono de fibra óptica de velocidad */}
        <Box
          sx={{
            width: size === 'small' ? 48 : 68,
            height: size === 'small' ? 48 : 68,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            boxShadow: '0 0 25px rgba(0, 198, 255, 0.75), inset 0 0 12px rgba(255,255,255,0.5)',
            zIndex: 2,
            animation: 'signalWave 2s infinite ease-in-out'
          }}
        >
          <svg
            width={size === 'small' ? '26' : '36'}
            height={size === 'small' ? '26' : '36'}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* Núcleo de fibra óptica e hilos transmisores de luz */}
            <circle cx="12" cy="12" r="3.5" fill="currentColor" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" strokeWidth="2.5" />
            <path d="M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" strokeWidth="2" opacity="0.8" />
          </svg>
        </Box>
      </Box>

      {/* Barra Láser de Velocidad */}
      <Box
        sx={{
          width: size === 'small' ? 120 : 170,
          height: 4,
          borderRadius: 2,
          bgcolor: 'rgba(0, 198, 255, 0.15)',
          overflow: 'hidden',
          mb: 2,
          position: 'relative'
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent 0%, #00c6ff 50%, transparent 100%)',
            backgroundSize: '200% 100%',
            animation: 'laserStream 1.4s infinite linear'
          }}
        />
      </Box>

      {/* Texto de Carga */}
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          fontFamily: '"Outfit", "Inter", sans-serif',
          letterSpacing: '0.4px',
          background: 'linear-gradient(90deg, #00c6ff, #0072ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textAlign: 'center',
          fontSize: size === 'small' ? '0.95rem' : '1.1rem'
        }}
      >
        {text}
      </Typography>

      {subtext && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: 0.5,
            fontSize: '0.82rem',
            textAlign: 'center',
            opacity: 0.85
          }}
        >
          {subtext}
        </Typography>
      )}
    </Box>
  );
};

export default FiberLoader;
